import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import { autoInvestSuccessPath } from 'components/Routes'
import DefaultLayout from 'components/layout/DefaultLayout'
import { useFormik } from 'formik'
import useSpotPrice from 'hooks/useSpotPrice'
import { useMutateCreateAutoInvest } from 'hooks/useAutoInvest'
import useProducts from 'hooks/useProducts'
import { useMemo } from 'react'
import { NumericFormat } from 'react-number-format'
import { useNavigate } from 'react-router-dom'
import theme from 'utils/theme'
import { generateUsername, roundDownDollarsAndCents } from 'utils/util'
import * as Yup from 'yup'

interface FormValues {
  when: string
  what: string
  vault: string
  amount: number
  investBy: string
}

const initialValues: FormValues = {
  when: '',
  what: '',
  vault: '',
  investBy: 'unit',
  amount: 0.001
}

const validationSchema = Yup.object().shape({
  when: Yup.string().required(),
  what: Yup.string().required(),
  vault: Yup.string().required(),
  investBy: Yup.string().required(),
  amount: Yup.number()
    .required('This field is required')
    .when('investBy', {
      is: 'unit',
      then: schema => schema.min(0.001, 'Minimum is 0.001g'),
      otherwise: schema => schema.min(1, 'Minimum is $1')
    })
})

export default function AddAutoInvest(): JSX.Element {
  const { user, setToast, refreshCurrentUser } = useAuth()
  const { products, loading: productsLoading } = useProducts()

  const navigate = useNavigate()

  const listVault = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      user?.vaults.map((vault: any) =>
        vault.name ? vault : { ...vault, name: generateUsername(user) }
      ),
    [user]
  )

  const listProduct = useMemo(
    () => products.filter(product => product.tradable),
    [products]
  )

  const { mutate: createAutoInvest, isLoading: loading } =
    useMutateCreateAutoInvest()

  const {
    errors,
    touched,
    values,
    handleChange,
    setFieldValue,
    handleBlur,
    handleSubmit
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values: FormValues) => {
      localStorage.setItem('investBy', JSON.stringify(values.investBy))
      createAutoInvest(
        {
          // Unit mode: user enters grams, API expects mg — multiply by 1000.
          // Dollar mode: user enters dollars, sent as-is.
          amount:
            values.investBy === 'unit'
              ? Math.round(Number(values.amount) * 1000)
              : Number(values.amount),
          duration: values.when,
          productId: Number(values.what),
          vaultId: Number(values.vault),
          investBy: values.investBy
        },
        {
          onSuccess: async () => {
            // The AutoInvest list renders user.autoInvests, which is the CACHED
            // /user/me payload — creating a schedule does not change it, so the new
            // one only appeared after a hard refresh. Thea, 11 Aug: "expectation when
            // I create an auto investment then click 'Got it' should show under auto
            // invest. Reality is I need to r5 to see the new auto investmnet."
            //
            // Under Rails this was an Apollo query and the mutation invalidated its
            // cache; the REST port keeps the user in context, so it has to be asked
            // for again. Awaited before navigating, so the list is built from a
            // payload that already has the schedule in it.
            await refreshCurrentUser()
            setToast({
              open: true,
              message: 'Create Auto Investment successful!',
              type: 'success',
              duration: 3000
            })
            navigate(autoInvestSuccessPath())
          },
          onError: (error: unknown) => {
            const message =
              error instanceof Error ? error.message : 'An error occurred'
            setToast({
              open: true,
              message,
              type: 'error',
              duration: 3000
            })
          }
        }
      )
    }
  })

  const [, { data: latestSpotPriceData }] = useSpotPrice({
    productId: values.what ? Number(values.what) : 0
  })

  const latestSpotPrice = latestSpotPriceData

  const handleInvestChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = (event.target as HTMLInputElement).value ?? 'dollar'
    setFieldValue('investBy', selected)
    setFieldValue('amount', selected === 'dollar' ? 20 : 0.001)
  }

  const handleIncreaseUnit = () => {
    setFieldValue('amount', Number(values.amount) + 0.001)
  }

  const handleDecreaseUnit = () => {
    setFieldValue('amount', Math.max(Number(values.amount) - 0.001, 0.001))
  }

  const renderInput = () => {
    if (values.investBy === 'dollar') {
      return (
        <>
          <TextField
            fullWidth
            required
            type='number'
            id='dollar'
            label='Maximum amount'
            name='amount'
            value={values.amount}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(errors.amount && touched.amount)}
            helperText={
              Boolean(errors.amount && touched.amount) && errors.amount
            }
            InputLabelProps={{
              style: {
                left: 0
              }
            }}
            InputProps={{
              startAdornment: (
                <Typography mt={1.25} ml={1}>
                  $
                </Typography>
              ),
              inputProps: {
                style: {
                  paddingLeft: '2px'
                }
              }
            }}
          />

          <Typography variant='body2'>
            Pro tip: ensure the amount is greater than the minimum product
            purchase amount based on the current price.
          </Typography>
        </>
      )
    }

    return (
      <NumericFormat
        thousandSeparator
        onValueChange={({ value }) =>
          handleChange({ target: { name: 'amount', value } })
        }
        onBlur={handleBlur}
        valueIsNumericString
        value={values.amount}
        fullWidth
        required
        label='Maximum units'
        name='amount'
        suffix=' g'
        error={Boolean(errors.amount && touched.amount)}
        helperText={
          Boolean(errors.amount && touched.amount)
            ? errors.amount
            : `Currently 1g is $${
                latestSpotPrice
                  ? roundDownDollarsAndCents(
                      Number(latestSpotPrice?.midpointPerMg) * 1000
                    ).toFixed(2)
                  : '0.00'
              } & it frequently changes`
        }
        InputLabelProps={{
          style: {
            left: 0
          }
        }}
        FormHelperTextProps={{
          style: {
            color: Boolean(errors.amount && touched.amount)
              ? theme.palette.error.main
              : theme.palette.primary.main
          }
        }}
        InputProps={{
          endAdornment: (
            <Box>
              <IconButton
                size='small'
                sx={{ p: 0 }}
                onClick={handleIncreaseUnit}
              >
                <ArrowDropUpIcon fontSize='small' />
              </IconButton>
              <IconButton
                size='small'
                sx={{ p: 0 }}
                onClick={handleDecreaseUnit}
              >
                <ArrowDropDownIcon fontSize='small' />
              </IconButton>
            </Box>
          ),

          style: {
            lineHeight: '1rem'
          }
        }}
        customInput={TextField}
      />
    )
  }

  return (
    <DefaultLayout heading='' showBackIcon loading={productsLoading}>
      <form noValidate onSubmit={handleSubmit}>
        <Grid2 container rowGap={3}>
          <Grid2 size={{ xs: 12 }}>
            <FormControl fullWidth error={!!(errors.when && touched.when)}>
              <InputLabel required id='when' sx={{ left: '0' }}>
                When?
              </InputLabel>
              <Select
                labelId='when'
                label=''
                name='when'
                value={values.when}
                onChange={handleChange}
                error={!!(errors.when && touched.when)}
              >
                <MenuItem value='weekly'>Weekly</MenuItem>
                <MenuItem value='fortnightly'>Fortnightly</MenuItem>
                <MenuItem value='beginning_of_month'>
                  Beginning of Month
                </MenuItem>
              </Select>
            </FormControl>
            {errors.when && touched.when && (
              <Typography variant='body2' color='error'>
                {errors?.when}
              </Typography>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <FormControl fullWidth error={!!(errors.what && touched.what)}>
              <InputLabel required id='what' sx={{ left: '0' }}>
                What?
              </InputLabel>
              <Select
                labelId='what'
                label=''
                name='what'
                value={values.what}
                onChange={handleChange}
                error={!!(errors.what && touched.what)}
              >
                {listProduct?.map(product => (
                  <MenuItem value={product?.id} key={product?.id}>
                    {product?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.what && touched.what && (
              <Typography variant='body2' color='error'>
                {errors?.what}
              </Typography>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <FormControl fullWidth error={!!(errors.vault && touched.vault)}>
              <InputLabel required id='vault' sx={{ left: '0' }}>
                Vault
              </InputLabel>
              <Select
                labelId='vault'
                label=''
                name='vault'
                value={values.vault}
                onChange={handleChange}
                error={!!(errors.vault && touched.vault)}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {listVault?.map((vault: any) => {
                  return (
                    <MenuItem value={vault.id} key={vault.id}>
                      {vault?.name}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            {errors.vault && touched.vault && (
              <Typography variant='body2' color='error'>
                {errors?.vault}
              </Typography>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <FormControl
              defaultValue={values.investBy ?? null}
              disabled={!values.what}
            >
              <RadioGroup
                defaultValue={values.investBy ?? null}
                value={values.investBy ?? null}
                onChange={handleInvestChange}
                name='investBy'
              >
                <FormControlLabel
                  value='unit'
                  control={<Radio />}
                  label='Invest by units'
                />
                <FormControlLabel
                  value='dollar'
                  control={<Radio />}
                  label='Invest by $'
                />
              </RadioGroup>
            </FormControl>
          </Grid2>

          {values.what && renderInput()}

          <Grid2 mt={0} size={{ xs: 12 }}>
            <Button
              fullWidth
              variant='outlined'
              type='submit'
              disabled={loading}
              loading={loading}
            >
              CREATE AUTO INVESTMENT
            </Button>
            <Typography textAlign='center' variant='body2' color='primary'>
              Tip: Auto invest uses money from your wallet, so keep it topped up
            </Typography>
          </Grid2>
        </Grid2>
      </form>
    </DefaultLayout>
  )
}
